import * as THREE from "three";
import { sideLimit, type Level } from "../level";
import type { CameraRig } from "../settings";
import { wrapAngle } from "../sim/math";
import type { Skater } from "../skater";

/** On foot (#19) the camera comes in closer and lower than the rig's skating chase view (fractions of its distance and height)... */
const FOOT_DIST = 0.7;
const FOOT_HEIGHT = 0.7;
/** ...and swings round to the way you face quicker (1/s; skating it's 3), to keep up with turning on the spot. */
const FOOT_TURN = 4;

/** The chase camera's field of view, in degrees (the slow-mo finisher narrows it for a moment, #118). */
export const CHASE_FOV = 68;

/**
 * Held upright (#235), a fixed vertical view leaves little across (68° tall
 * is only 35° across at 390 x 844), so the same turn sweeps the picture
 * about twice as far and steering reads as twitchy. On a screen taller
 * than it's wide the vertical view opens up until the view across is
 * `UPRIGHT_ACROSS`, never past `UPRIGHT_FOV_MAX` tall (more fish-eyes).
 */
export const UPRIGHT_ACROSS = 78;
export const UPRIGHT_FOV_MAX = 92;
/** How fast the view eases to a new shape after a rotate (1/s). */
export const FOV_EASE = 5;

const DEG = Math.PI / 180;

/** The view across (degrees) of a vertical view `fov` degrees tall on a screen `aspect` wide per unit tall. */
export function acrossFov(fov: number, aspect: number): number {
  return (2 * Math.atan(Math.tan((fov * DEG) / 2) * aspect)) / DEG;
}

/**
 * The chase camera's vertical view (degrees) for a screen `aspect` wide per
 * unit tall: exactly `CHASE_FOV` on a screen as wide as it's tall or wider;
 * upright, tall enough to show `UPRIGHT_ACROSS` across, capped. The hill's
 * speed widening (#194) goes on top of it.
 */
export function chaseFov(aspect: number): number {
  if (aspect >= 1) return CHASE_FOV;
  const tall = (2 * Math.atan(Math.tan((UPRIGHT_ACROSS * DEG) / 2) / aspect)) / DEG;
  return Math.min(UPRIGHT_FOV_MAX, Math.max(CHASE_FOV, tall));
}

/**
 * On a hill (#194) the camera shows the drop ahead: it looks `ahead` metres
 * further down the road the faster you go (and down the slope with it),
 * sits `lift` metres higher, and the view widens by `fov` degrees toward
 * top speed, for a sense of speed. On a phone (`roomy`, #198) it also sits
 * `back` metres further back and a little higher, so more of the road ahead
 * shows over the touch keys; on a tall screen (`tall`, #213) higher, further
 * back and looking further ahead still.
 */
export function hillView(speed: number, roomy = false, tall = false): { ahead: number; lift: number; fov: number; back: number } {
  const t = Math.min(1, Math.max(0, speed / 26));
  return {
    ahead: 2 + 6 * t + (tall ? HILL_TALL_AHEAD : 0),
    lift: 0.7 + 0.5 * t + (tall ? HILL_TALL_LIFT : roomy ? HILL_ROOMY_LIFT : 0),
    fov: Math.round(14 * t * t * 10) / 10,
    back: tall ? HILL_TALL_BACK : roomy ? HILL_ROOMY_BACK : 0,
  };
}

/** How much further back (m) and higher (m) the hill's camera sits on a phone (#198). */
const HILL_ROOMY_BACK = 1.2;
const HILL_ROOMY_LIFT = 0.6;
/**
 * ...and on a screen taller than it's wide (#213, a phone held upright):
 * higher and further back still, looking further down the road, so the road
 * fills the height and you see well ahead.
 */
const HILL_TALL_BACK = 1.4;
const HILL_TALL_LIFT = 1.0;
const HILL_TALL_AHEAD = 4;

/**
 * The chase camera (THPS style): behind the skater, following the direction
 * of travel rather than the way the board points, pulled back and held level
 * on vert, closer and lower on foot, and kept inside the fence and under an
 * indoor roof.
 */
export class ChaseCamera {
  readonly camera = new THREE.PerspectiveCamera(CHASE_FOV, 1, 0.1, 400);
  /** A phone-sized screen (#198): on a hill the camera sits further back and higher. */
  roomy = false;
  /** A screen taller than it's wide (#213): on the hill the camera frames the road for it. */
  tall = false;
  /** Unit heading the camera looks along, on the ground plane. */
  private dir = new THREE.Vector3(0, 0, -1);
  private pos = new THREE.Vector3();
  private look = new THREE.Vector3();
  // Scratch vectors, so a frame allocates nothing.
  private target = new THREE.Vector3();
  private want = new THREE.Vector3();

  /** Turns the camera to look along a heading on the ground (x, z); `snap` applies it. */
  face(x: number, z: number): void {
    this.dir.set(x, 0, z);
  }

  /**
   * Puts the camera straight behind the skater, no easing, and applies it
   * now: behind the start screen or the pause menu no update runs.
   */
  snap(s: Skater, level: Level, roof: number | null): void {
    this.look.set(s.x, s.y + 1.1, s.z);
    this.pos.copy(this.look).addScaledVector(this.dir, -5);
    this.pos.y += 1.6;
    this.keepInside(level, roof);
    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.look);
  }

  /** Moves the camera `dz` along z and `dy` up with the world under it (#195: the hill's road moving back up), easing on as before. */
  shift(dz: number, dy: number): void {
    for (const v of [this.pos, this.look, this.camera.position]) {
      v.z += dz;
      v.y += dy;
    }
  }

  /** Eases toward the skater for one rendered frame. */
  update(s: Skater, dt: number, rig: CameraRig, level: Level, roof: number | null): void {
    // Follow the direction of travel (THPS style), not the way the board points.
    let tx = 0;
    let tz = 0;
    if (s.mode === "foot") {
      // Behind the way you face, walking, backing up or standing still.
      const f = s.forward;
      tx = f.x;
      tz = f.z;
    } else if (s.mode === "ground") {
      const f = s.forward;
      const sign = s.speed < -0.5 ? -1 : 1;
      tx = f.x * sign;
      tz = f.z * sign;
    } else if (s.mode === "grind" && s.grind) {
      const g = s.grind;
      tx = (g.rail.b.x - g.rail.a.x) * g.dir;
      tz = (g.rail.b.z - g.rail.a.z) * g.dir;
    } else if (Math.hypot(s.vx, s.vz) > 2) {
      tx = s.vx;
      tz = s.vz;
    }
    const len = Math.hypot(tx, tz);
    if (len > 1e-3 && s.mode !== "bail") {
      // Ease the heading angle, not the vector: lerping toward the exact
      // opposite direction (rolling away fakie) shrinks the vector and
      // normalize() snaps it back, so the camera would never come around.
      const current = Math.atan2(this.dir.x, this.dir.z);
      const rate = s.mode === "foot" ? FOOT_TURN : 3;
      const turn = wrapAngle(Math.atan2(tx, tz) - current) * (1 - Math.exp(-rate * dt));
      this.dir.set(Math.sin(current + turn), 0, Math.cos(current + turn));
    }

    // On vert, hold the camera level and pull back so the whole air is visible.
    const onVert = s.vertAxis !== null;
    const foot = s.mode === "foot";
    const dist = onVert ? rig.vertDist : foot ? rig.dist * FOOT_DIST : rig.dist;
    const height = onVert ? rig.vertHeight : foot ? rig.height * FOOT_HEIGHT : rig.height;
    const target = this.target.set(s.x, s.y + 1.0, s.z);
    const want = this.want.copy(target).addScaledVector(this.dir, -dist);
    want.y = Math.max(want.y, s.y) + height;
    const grade = level.hill?.grade;
    if (grade !== undefined && !foot) {
      // On a hill (#194): up above the road behind you, looking down the drop ahead.
      const view = hillView(s.horizontalSpeed, this.roomy, this.tall);
      want.addScaledVector(this.dir, -view.back);
      want.y += grade * (dist + view.back) * Math.max(0, this.dir.z) + view.lift;
      target.addScaledVector(this.dir, view.ahead);
      target.y -= grade * view.ahead * Math.max(0, this.dir.z);
    }
    this.look.lerp(target, 1 - Math.exp(-12 * dt));
    this.pos.lerp(want, 1 - Math.exp(-6 * dt));
    this.keepInside(level, roof);
    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.look);
  }

  /** Sets the field of view (degrees), rebuilding the projection only when it changes. */
  setFov(fov: number): void {
    if (this.camera.fov === fov) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  /** Inside the walls (out over the sand and sea on a beach side, #82), and under the roof indoors. */
  private keepInside(level: Level, roof: number | null): void {
    const m = 0.6;
    this.pos.x = Math.max(-sideLimit(level, "west", m, "open"), Math.min(sideLimit(level, "east", m, "open"), this.pos.x));
    this.pos.z = Math.max(-sideLimit(level, "north", m, "open"), Math.min(sideLimit(level, "south", m, "open"), this.pos.z));
    if (roof !== null) this.pos.y = Math.min(this.pos.y, roof - 1);
  }
}
